import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { api, apiErrorMessage } from "@/lib/api";
import { Mic, MicOff, Volume2, VolumeX, PhoneOff, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Voice AI Concierge — hands-free chat that uses the browser's native
 * SpeechRecognition (STT) + SpeechSynthesis (TTS) so nobody pays for the
 * voice layer.  Reasoning still runs through /api/ai/chat with the
 * `voice_concierge` system prompt (short, spoken-friendly replies).
 *
 * If the browser doesn't ship SpeechRecognition (Safari desktop), we fall
 * back to a text box so the page is still useful — the assistant still
 * speaks its reply.
 */
const SR = typeof window !== "undefined"
  ? (window.SpeechRecognition || window.webkitSpeechRecognition)
  : null;

export default function VoiceConciergePage() {
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith("/admin");
  const [listening, setListening] = useState(false);
  const [thinking, setThinking] = useState(false);
  const [muted, setMuted] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [reply, setReply] = useState("");
  const [history, setHistory] = useState([]); // [{role, content}]
  const [textInput, setTextInput] = useState("");
  const recognitionRef = useRef(null);
  const sessionRef = useRef(`voice-${Date.now()}`);
  const supported = !!SR;

  const speak = (text) => {
    if (muted || !text) return;
    try {
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(text);
      u.rate = 1.02; u.pitch = 1.0;
      // Prefer a natural-sounding en-US voice if available
      const voices = window.speechSynthesis.getVoices();
      const pref = voices.find((v) => /en[-_]us/i.test(v.lang) && /female|samantha|karen|zoe/i.test(v.name));
      if (pref) u.voice = pref;
      window.speechSynthesis.speak(u);
    } catch (err) { if (typeof console !== 'undefined') console.debug('pages/ai/VoiceConciergePage.jsx: swallowed', err); }
  };

  const askAI = async (userText) => {
    if (!userText?.trim()) return;
    setHistory((h) => [...h, { role: "user", content: userText }]);
    setReply("");
    setThinking(true);
    try {
      const { data } = await api.post("/ai/chat", {
        agent: "voice_concierge",
        message: userText,
        session_id: sessionRef.current,
      });
      const answer = (data.reply || "").trim();
      setReply(answer);
      setHistory((h) => [...h, { role: "assistant", content: answer }]);
      speak(answer);
    } catch (e) {
      const msg = apiErrorMessage(e);
      toast.error(msg);
      speak("Sorry, I hit a snag. Please try again in a moment.");
    }
    setThinking(false);
  };

  const startListening = () => {
    if (!supported) return;
    if (!recognitionRef.current) {
      const rec = new SR();
      rec.continuous = false;
      rec.interimResults = true;
      rec.lang = "en-US";
      rec.onresult = (e) => {
        const chunks = [];
        for (let i = e.resultIndex; i < e.results.length; i++) {
          chunks.push(e.results[i][0].transcript);
        }
        setTranscript(chunks.join(" "));
      };
      rec.onerror = (e) => {
        if (e.error !== "no-speech" && e.error !== "aborted") {
          toast.error(`Mic error: ${e.error}`);
        }
        setListening(false);
      };
      rec.onend = async () => {
        setListening(false);
        const final = transcript.trim() || (recognitionRef.current?._final || "");
        if (final) {
          setTranscript("");
          await askAI(final);
        }
      };
      recognitionRef.current = rec;
    }
    try {
      window.speechSynthesis.cancel();
      setTranscript("");
      recognitionRef.current.start();
      setListening(true);
    } catch (err) { if (typeof console !== 'undefined') console.debug('pages/ai/VoiceConciergePage.jsx: swallowed', err); }
  };

  const stopListening = () => {
    try { recognitionRef.current?.stop(); } catch (err) { if (typeof console !== 'undefined') console.debug('pages/ai/VoiceConciergePage.jsx: swallowed', err); }
    setListening(false);
  };

  const sendText = async (e) => {
    e?.preventDefault?.();
    const t = textInput.trim();
    if (!t) return;
    setTextInput("");
    await askAI(t);
  };

  const hangUp = () => {
    try { window.speechSynthesis.cancel(); } catch (err) { if (typeof console !== 'undefined') console.debug('pages/ai/VoiceConciergePage.jsx: swallowed', err); }
    stopListening();
    setHistory([]);
    setReply("");
    sessionRef.current = `voice-${Date.now()}`;
  };

  // Cleanup on unmount
  useEffect(() => () => {
    try { window.speechSynthesis.cancel(); } catch (err) { if (typeof console !== 'undefined') console.debug('pages/ai/VoiceConciergePage.jsx: swallowed', err); }
    try { recognitionRef.current?.stop(); } catch (err) { if (typeof console !== 'undefined') console.debug('pages/ai/VoiceConciergePage.jsx: swallowed', err); }
  }, []);

  const cardBg = isAdmin ? "border-white/10 bg-card" : "bg-white";
  const textCol = isAdmin ? "text-white" : "text-zinc-900";
  const softCol = isAdmin ? "text-zinc-400" : "text-zinc-500";

  return (
    <div className="max-w-3xl">
      <PageHeader
        eyebrow="AI · Voice concierge"
        title="Voice AI Concierge"
        subtitle="Tap the mic and ask hands-free. I'll book amenities and log tickets for you."
      />
      <Card className={cardBg + " p-6"}>
        {!supported && (
          <div className={"text-xs mb-4 " + softCol}>
            Voice input isn&apos;t supported in this browser (try Chrome or Edge). You can still
            type — I&apos;ll speak the reply out loud.
          </div>
        )}

        {/* Mic orb */}
        <div className="flex flex-col items-center py-6">
          <div className="relative">
            <div className={
              "absolute inset-0 rounded-full blur-2xl transition-all duration-500 " +
              (listening ? "bg-orange-500/40 scale-125" : thinking ? "bg-indigo-500/40 scale-110" : "bg-indigo-500/20 scale-100")
            } />
            <button
              type="button"
              onClick={listening ? stopListening : startListening}
              disabled={!supported || thinking}
              data-testid="voice-mic-btn"
              className={
                "relative h-32 w-32 rounded-full flex items-center justify-center transition-all border-4 " +
                (listening
                  ? "bg-orange-500 border-orange-300 shadow-[0_0_60px_rgba(249,115,22,0.6)]"
                  : thinking
                    ? "bg-indigo-500 border-indigo-300 shadow-[0_0_50px_rgba(99,102,241,0.5)]"
                    : "bg-indigo-500/90 hover:bg-indigo-500 border-indigo-300 shadow-[0_0_40px_rgba(99,102,241,0.4)]")
              }
              aria-label={listening ? "Stop listening" : "Start talking"}
            >
              {thinking
                ? <Loader2 className="h-10 w-10 text-white animate-spin" />
                : listening
                  ? <MicOff className="h-10 w-10 text-white" />
                  : <Mic className="h-10 w-10 text-white" />}
            </button>
          </div>
          <div className={"mt-5 text-center text-sm " + softCol}>
            {listening
              ? "Listening… speak naturally, tap to stop."
              : thinking
                ? "Thinking…"
                : supported
                  ? "Tap the mic and speak — I'll respond out loud."
                  : "Type your question below."}
          </div>
          {transcript && (
            <div className={"mt-3 text-sm italic " + softCol} data-testid="voice-transcript">
              &ldquo;{transcript}&rdquo;
            </div>
          )}
        </div>

        {/* Latest AI reply */}
        {reply && (
          <div
            className={
              "rounded-lg border p-4 mb-4 " +
              (isAdmin ? "border-indigo-500/40 bg-indigo-500/[0.08]" : "border-indigo-200 bg-indigo-50")
            }
            data-testid="voice-reply"
          >
            <div className={"flex items-center gap-2 text-[11px] uppercase tracking-widest " + (isAdmin ? "text-indigo-300" : "text-indigo-700")}>
              <Sparkles className="h-3 w-3" /> Nova replied
            </div>
            <div className={"mt-2 text-sm leading-relaxed " + textCol}>{reply}</div>
          </div>
        )}

        {/* Controls */}
        <div className="flex flex-wrap items-center gap-2 justify-center mb-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setMuted((m) => !m)}
            className={isAdmin ? "border-white/10" : "border-zinc-200"}
            data-testid="voice-mute-btn"
          >
            {muted ? <VolumeX className="h-3.5 w-3.5 mr-1" /> : <Volume2 className="h-3.5 w-3.5 mr-1" />}
            {muted ? "Un-mute" : "Mute voice"}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={hangUp}
            className={isAdmin ? "border-white/10" : "border-zinc-200"}
            data-testid="voice-hangup-btn"
          >
            <PhoneOff className="h-3.5 w-3.5 mr-1" /> End call
          </Button>
        </div>

        {/* Fallback text input */}
        <form onSubmit={sendText} className="flex items-center gap-2">
          <input
            type="text"
            value={textInput}
            onChange={(e) => setTextInput(e.target.value)}
            placeholder="Or type here…"
            data-testid="voice-text-input"
            className={
              "flex-1 rounded-md px-3 py-2 text-sm " +
              (isAdmin
                ? "bg-white/[0.03] border border-white/10 text-white placeholder:text-zinc-500"
                : "bg-zinc-50 border border-zinc-200 text-zinc-900 placeholder:text-zinc-400")
            }
          />
          <Button
            type="submit"
            disabled={!textInput.trim() || thinking}
            className="bg-orange-500 hover:bg-orange-600 text-white"
            data-testid="voice-text-send"
          >
            Send
          </Button>
        </form>

        {/* Transcript log */}
        {history.length > 0 && (
          <div className={"mt-6 pt-4 border-t " + (isAdmin ? "border-white/10" : "border-zinc-200")}>
            <div className={"text-[11px] uppercase tracking-widest mb-2 " + softCol}>Conversation</div>
            <div className="space-y-1.5 max-h-56 overflow-y-auto" data-testid="voice-history">
              {history.map((m, i) => (
                <div key={i} className={"text-xs " + (m.role === "user" ? textCol : softCol)}>
                  <span className="font-semibold">{m.role === "user" ? "You" : "Nova"}:</span> {m.content}
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
